<template>
  <client-only>
    <div class="tinymce-editor qa-editor" :class="type" :exceeded-chars="exceededCharsCheck">
      <ul class="editor-language-dropdown-options">
        <li v-for="(language, key) in codeLanguages" :key="key" @click="selectCodeLanguage(key, $event)">
          {{ language }}
        </li>
      </ul>
      <Editor
        ref="qa-editor"
        :init="EDITOR_INIT"
        :tinymce-script-src="$config.tinymceUrl"
        :inline="true"
        v-bind="$attrs"
        v-on="$listeners"
        @onFocusIn="focusIn"
      />
      <span class="char-limit" :exceeded-chars="exceededCharsCheck">
        {{ remainingChars }}
      </span>
      <div :id="`editor-toolbar${_uid}`" class="editor-toolbar" />
    </div>
  </client-only>
</template>

<script>
import Editor from '@tinymce/tinymce-vue';
import codeblock from '../plugins/editor/codeblock';
import codeblockMixin from '../mixins/editor/codeblock';
import emojiMixin from '../mixins/editor/emoji';
import events from '../mixins/events';

const emojis = [
  'smile',
  'innocent',
  'relieved',
  'sunglasses',
  'wink',
  'smile-licky',
  'wink-licky',
  'tongue-out',
  'stick-out-tongue',
  'beaming-face',
  'grinning',
  'grinning-smiling-eyes',
  'grinning-smiling-squinting-eyes',
  'laughing',
  'kissing',
  'kissing-closed-eyes',
  'inlove',
  'shy',
  'surprised',
  'anguished',
  'frowning',
  'grimacing',
  'fearful',
  'anxious',
  'screaming',
  'confounded',
  'dizzy',
  'neutral',
  'without-mouth',
  'confused',
  'frowning-eyebrows',
  'disappointed',
  'sad',
  'persevering',
  'confounded-normal',
  'distraught',
  'weary',
  'expressionless',
  'unamused',
  'sleepy',
  'crying',
  'crying-loudly',
  'downcast',
  'steamy',
  'angry',
  'angry-red',
  'smirk',
  'devil',
  'mask',
  'smoked',
];

export default {
  components: { Editor },
  mixins: [codeblockMixin, emojiMixin, events],
  props: {
    type: { type: String, default: '', required: false },
  },
  data() {
    return {
      remainingChars: 0,
      startCount: 2000,
      editor: null,
      editLinkPopup: { show: false, link: '', title: '' },
      clickOutside: false,
      EDITOR_INIT: {
        max_height: 300,
        menubar: false,
        statusbar: true,
        paste_data_images: false,
        convert_urls: false,
        inline_boundaries: false,
        content_style: '.qa-editor img',
        // eslint-disable-next-line no-underscore-dangle
        fixed_toolbar_container: `#editor-toolbar${this._uid}`,
        plugins: ['link image wordcount autoresize noneditable paste autolink codeblock'],
        paste_block_drop: true,
        paste_as_text: true,
        paste_preprocess(plugin, args) {
          const argsRef = args;

          // eslint-disable-next-line no-useless-escape
          const urlRegex = /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/gi;
          const matches = [...argsRef.content.matchAll(urlRegex)];
          matches.forEach((match) => {
            const url = match[0];
            argsRef.content = argsRef.content.replace(
              url,
              `<a href="${url}" rel="noopener" data-mce-href="${url}">${url}</a>`,
            );
          });
        },
        toolbar: 'bold italic underline | link images | codeblock | emoticons_group',
        codesample_languages: [
          { text: 'HTML/XML', value: 'markup' },
          { text: 'JavaScript', value: 'javascript' },
          { text: 'CSS', value: 'css' },
          { text: 'PHP', value: 'php' },
          { text: 'Ruby', value: 'ruby' },
          { text: 'Python', value: 'python' },
          { text: 'Java', value: 'java' },
          { text: 'C', value: 'c' },
          { text: 'C#', value: 'csharp' },
          { text: 'C++', value: 'cpp' },
          { text: 'R', value: 'r' },
          { text: 'SQL', value: 'sql' },
        ],
        link_title: false,
        display_text: false,
        toolbar_persist: true,
        force_br_newlines: true,
        force_p_newlines: false,
        forced_root_block: '',
        valid_elements: '*[*]',
        extended_valid_elements: [
          '[class="emoji"], span[class="emoji"]',
          'a[href|target=_blank|class=external-anchor]',
          'img[src|selected|alt|class=post-image newest-image|style|width|height]',
        ],
        object_resizing: 'img',
        toolbar_groups: {
          emoticons_group: {
            icon: 'emoji',
            tooltip: 'Emoticons',
            items: emojis.join(' '),
          },
        },
        resize: false,
        contextmenu: false,
        mobile: {
          toolbar_mode: 'floating',
        },
        default_link_target: '_blank',
        target_list: false,
        setup: (editor) => {
          editor.on('init', () => {
            // eslint-disable-next-line no-undef
            const myContent = tinymce.activeEditor.getContent();
            if (myContent && myContent.includes('<pre')) {
              const block = document.querySelector('.mce-content-body pre');
              block.addEventListener('click', () => {
                const button = document.querySelector('.tox-toolbar__primary div:nth-child(3)');
                button.classList.add('btn-code');
              });
              const editorContent = document.querySelectorAll('.mce-content-body p');
              editorContent.forEach((elem) => {
                elem.addEventListener('click', () => {
                  const button = document.querySelector('.tox-toolbar__primary div:nth-child(3)');
                  button.classList.remove('btn-code');
                });
              });

              setTimeout(() => {
                const marker = editor.targetElm.querySelector('span[id^=mce_]');
                // eslint-disable-next-line no-undef
                const ed = tinyMCE.activeEditor;
                ed.selection.select(marker);
                ed.selection.collapse(false);
                ed.focus();
              }, 50);
            }
          });

          const setupImages = () => {
            editor.on('NodeChange', (e) => {
              if (e.element.nodeName === 'IMG') {
                this.clickImage(e.element);
              }
            });
          };

          setTimeout(() => {
            // charsCount();
            setupImages();
          }, 1000);

          const vm = this;

          // eslint-disable-next-line no-param-reassign
          editor.props = { vm: this, type: 'qa-hub' };
          // eslint-disable-next-line no-undef
          tinymce.PluginManager.add('codeblock', codeblock());

          // reject typing in the editor if the remaining chars is less than 0
          // editor.on(
          //   'keydown',
          //   (e) => !(this.remainingChars < 1 && [8, 13, 16, 17, 18, 20, 33, 34, 35, 36, 37, 38, 39, 40, 46].indexOf(e.keyCode) === -1),
          // );

          // character counter
          const charsCount = () => {
            const emojisLen = (editor.getContent().match(/<span class="emoji/g) || editor.getContent().match(/<img /g) || []).length;
            const { wordcount } = editor.plugins;
            this.remainingChars = this.startCount - +wordcount.body.getCharacterCount() - emojisLen;

            const hiddenSpaces = editor.getContent().match(/<span class="qa-emoji-hidden-space">&nbsp;<\/span>/g) || [];

            this.$root.$emit('qa-editor-chars-count', this.remainingChars);

            if (hiddenSpaces.length) {
              this.remainingChars += hiddenSpaces.length;
            }
          };

          editor.on('SetContent', (cb) => {
            charsCount();

            if (cb.content && cb.content.includes('class="post-image"')) {
              setTimeout(() => {
                editor.selection.collapse();
              }, 200);

              const newestImage = editor.getBody().querySelector('.post-image.newest-image');

              if (newestImage) {
                const parentDiv = newestImage.parentNode;

                // Check if the parent div contains anything other than a <br> tag
                if (parentDiv.children.length > 1 || parentDiv.innerHTML.trim() !== '<br>') {
                  // Create a new div for the image
                  const newDiv = document.createElement('div');
                  parentDiv.insertBefore(newDiv, newestImage);
                  newDiv.appendChild(newestImage);
                }

                editor.selection.setCursorLocation(newestImage, 0);

                vm.$nextTick(() => {
                  newestImage.classList.remove('newest-image');
                });
              }
            }

            setTimeout(() => {
              // eslint-disable-next-line no-undef
              const myContent = tinymce.activeEditor.getContent();
              if (myContent && myContent.includes('<pre')) {
                const block = document.querySelector('.mce-content-body pre');
                block.addEventListener('click', () => {
                  const button = document.querySelector('.tox-toolbar__primary div:nth-child(3)');
                  button.classList.add('btn-code');
                });
                const editorContent = document.querySelectorAll('.mce-content-body p');
                editorContent.forEach((elem) => {
                  elem.addEventListener('click', () => {
                    const button = document.querySelector('.tox-toolbar__primary div:nth-child(3)');
                    button.classList.remove('btn-code');
                  });
                });
              }
            }, 1000);
          });
          editor.on('keypress', () => {
            charsCount();
          });
          editor.on('keyup', () => {
            charsCount();
          });

          editor.ui.registry.addButton('images', {
            icon: 'image',
            tooltip: 'Add Image',
            onAction() {
              vm.addImageToEditor(editor);
            },
            onSetup(api) {
              editor.on('NodeChange', () => vm.nodeChangeHandler('images', editor, api));

              return function () {
                editor.off('NodeChange', () => vm.nodeChangeHandler('images', editor, api));
              };
            },
          });

          editor.ui.registry.addIcon('bold', '<svg width="20" height="21" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.75 8.55363C13.75 8.17061 13.6746 7.79134 13.528 7.43747C13.3814 7.0836 13.1666 6.76207 12.8957 6.49123C12.6249 6.2204 12.3034 6.00556 11.9495 5.85898C11.5956 5.7124 11.2164 5.63696 10.8333 5.63696H7.08333C6.86232 5.63696 6.65036 5.72476 6.49408 5.88104C6.3378 6.03732 6.25 6.24928 6.25 6.4703V14.8036C6.25 15.0246 6.3378 15.2366 6.49408 15.3929C6.65036 15.5492 6.86232 15.637 7.08333 15.637H10.8333C11.4139 15.6395 11.9819 15.4682 12.4643 15.1451C12.9467 14.8221 13.3213 14.362 13.5401 13.8243C13.7588 13.2865 13.8115 12.6955 13.6915 12.1275C13.5715 11.5594 13.2842 11.0403 12.8667 10.637C13.1453 10.3661 13.3669 10.0422 13.5186 9.68445C13.6703 9.32669 13.749 8.94222 13.75 8.55363ZM10.8333 13.9703H7.91667V11.4703H10.8333C11.1649 11.4703 11.4828 11.602 11.7172 11.8364C11.9516 12.0708 12.0833 12.3888 12.0833 12.7203C12.0833 13.0518 11.9516 13.3698 11.7172 13.6042C11.4828 13.8386 11.1649 13.9703 10.8333 13.9703ZM10.8333 9.80363H7.91667V7.30363H10.8333C11.1649 7.30363 11.4828 7.43533 11.7172 7.66975C11.9516 7.90417 12.0833 8.22211 12.0833 8.55363C12.0833 8.88515 11.9516 9.20309 11.7172 9.43751C11.4828 9.67193 11.1649 9.80363 10.8333 9.80363Z" fill="#000C1F"/></svg>');
          editor.ui.registry.addIcon('italic', '<svg width="20" height="21" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M14.1667 5.63696H9.16667C8.94565 5.63696 8.73369 5.72476 8.57741 5.88104C8.42113 6.03732 8.33333 6.24928 8.33333 6.4703C8.33333 6.69131 8.42113 6.90327 8.57741 7.05955C8.73369 7.21583 8.94565 7.30363 9.16667 7.30363H10.4333L7.76667 13.9703H5.83333C5.61232 13.9703 5.40036 14.0581 5.24408 14.2144C5.0878 14.3707 5 14.5826 5 14.8036C5 15.0246 5.0878 15.2366 5.24408 15.3929C5.40036 15.5492 5.61232 15.637 5.83333 15.637H10.8333C11.0543 15.637 11.2663 15.5492 11.4226 15.3929C11.5789 15.2366 11.6667 15.0246 11.6667 14.8036C11.6667 14.5826 11.5789 14.3707 11.4226 14.2144C11.2663 14.0581 11.0543 13.9703 10.8333 13.9703H9.56667L12.2333 7.30363H14.1667C14.3877 7.30363 14.5996 7.21583 14.7559 7.05955C14.9122 6.90327 15 6.69131 15 6.4703C15 6.24928 14.9122 6.03732 14.7559 5.88104C14.5996 5.72476 14.3877 5.63696 14.1667 5.63696Z" fill="#000C1F"/></svg>');
          editor.ui.registry.addIcon('underline', '<svg width="20" height="21" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10 12.9703C10.8841 12.9703 11.7319 12.6191 12.357 11.994C12.9821 11.3689 13.3333 10.521 13.3333 9.63696V6.30363C13.3333 6.12682 13.2631 5.95725 13.1381 5.83223C13.013 5.7072 12.8435 5.63696 12.6667 5.63696C12.4899 5.63696 12.3203 5.7072 12.1953 5.83223C12.0702 5.95725 12 6.12682 12 6.30363V9.63696C12 10.1674 11.7893 10.6761 11.4142 11.0512C11.0391 11.4262 10.5304 11.637 10 11.637C9.46957 11.637 8.96086 11.4262 8.58579 11.0512C8.21071 10.6761 8 10.1674 8 9.63696V6.30363C8 6.12682 7.92976 5.95725 7.80474 5.83223C7.67971 5.7072 7.51014 5.63696 7.33333 5.63696C7.15652 5.63696 6.98695 5.7072 6.86193 5.83223C6.7369 5.95725 6.66667 6.12682 6.66667 6.30363V9.63696C6.66667 10.521 7.01786 11.3689 7.64298 11.994C8.2681 12.6191 9.11595 12.9703 10 12.9703ZM13.3333 14.3036H6.66667C6.48986 14.3036 6.32029 14.3739 6.19526 14.4989C6.07024 14.6239 6 14.7935 6 14.9703C6 15.1471 6.07024 15.3167 6.19526 15.4417C6.32029 15.5667 6.48986 15.637 6.66667 15.637H13.3333C13.5101 15.637 13.6797 15.5667 13.8047 15.4417C13.9298 15.3167 14 15.1471 14 14.9703C14 14.7935 13.9298 14.6239 13.8047 14.4989C13.6797 14.3739 13.5101 14.3036 13.3333 14.3036Z" fill="#000C1F"/></svg>');
          editor.ui.registry.addIcon('link', '<svg width="20" height="21" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9.59366 12.631L10.0553 12.1742C10.1172 12.1171 10.1839 12.0314 10.2077 11.9791C9.84592 11.9553 9.47467 11.784 9.16529 11.4747C8.43231 10.7419 8.43706 9.6999 9.16529 8.96713L11.131 7.00674C11.8593 6.27397 12.9016 6.26921 13.6346 7.00198C14.3724 7.74427 14.3724 8.78632 13.6393 9.51432L12.7588 10.3851C12.854 10.7277 12.854 11.0941 12.7921 11.4462L14.2153 10.0139C15.2576 8.97188 15.2671 7.48732 14.2105 6.42624C13.1539 5.36991 11.6689 5.37942 10.6265 6.42148L8.58462 8.46275C7.54225 9.5048 7.53273 10.9894 8.58937 12.0457C8.89399 12.355 9.24145 12.5596 9.59366 12.631ZM5.7907 14.8483C6.84734 15.9046 8.33235 15.8951 9.37947 14.8483L11.4166 12.8118C12.4637 11.765 12.4732 10.2804 11.4166 9.22407C11.1072 8.91479 10.7598 8.71494 10.4123 8.63881L9.94111 9.10988C9.884 9.16697 9.82212 9.24311 9.79833 9.29544C10.1601 9.31924 10.5313 9.49054 10.8407 9.79982C11.5737 10.5326 11.5689 11.5746 10.8359 12.3074L8.87495 14.2678C8.14672 15.0006 7.09961 15.0053 6.37138 14.2726C5.63839 13.5255 5.63363 12.493 6.36662 11.7602L7.24715 10.8847C7.15196 10.5469 7.1472 10.1805 7.21384 9.82837L5.7907 11.2558C4.74358 12.3026 4.7293 13.7872 5.7907 14.8483Z" fill="#000C1F" stroke="#000C1F" stroke-width="0.5"/></svg>');
          editor.ui.registry.addIcon('image', '<svg width="20" height="21" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.8889 5.63696H6.11111C5.49746 5.63696 5 6.13442 5 6.74807V14.5259C5 15.1395 5.49746 15.637 6.11111 15.637H13.8889C14.5025 15.637 15 15.1395 15 14.5259V6.74807C15 6.13442 14.5025 5.63696 13.8889 5.63696Z" stroke="#000C1F" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/><path d="M8.05562 9.5258C8.51586 9.5258 8.88896 9.1527 8.88896 8.69246C8.88896 8.23223 8.51586 7.85913 8.05562 7.85913C7.59539 7.85913 7.22229 8.23223 7.22229 8.69246C7.22229 9.1527 7.59539 9.5258 8.05562 9.5258Z" stroke="#000C1F" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/><path d="M14.9999 12.3039V12.3039C13.4657 10.7698 10.9784 10.7698 9.4443 12.3039L6.11096 15.6372" stroke="#000C1F" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>');
          editor.ui.registry.addIcon('emoji', '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7 12.7632C10.3137 12.7632 13 10.0769 13 6.76318C13 3.44948 10.3137 0.763184 7 0.763184C3.68629 0.763184 1 3.44948 1 6.76318C1 10.0769 3.68629 12.7632 7 12.7632Z" stroke="#000C1F" stroke-linecap="round" stroke-linejoin="round"/><path d="M3.95386 7.68628C4.4154 9.34782 6.26155 10.3632 7.92309 9.90166C8.93847 9.53243 9.76924 8.70166 10.0462 7.68628" stroke="#000C1F" stroke-linecap="round" stroke-linejoin="round"/><path d="M4.96928 5.28622C4.86732 5.28622 4.78467 5.20357 4.78467 5.10161C4.78467 4.99965 4.86732 4.91699 4.96928 4.91699" stroke="#000C1F" stroke-linecap="round" stroke-linejoin="round"/><path d="M4.96924 4.78622C4.6931 4.78622 4.46924 5.01008 4.46924 5.28622C4.46924 5.56237 4.6931 5.78622 4.96924 5.78622V4.78622ZM4.96924 4.41699C4.6931 4.41699 4.46924 4.64085 4.46924 4.91699C4.46924 5.19313 4.6931 5.41699 4.96924 5.41699V4.41699ZM4.96924 5.78622C5.34734 5.78622 5.65385 5.47971 5.65385 5.10161H4.65385C4.65385 4.92743 4.79506 4.78622 4.96924 4.78622V5.78622ZM5.65385 5.10161C5.65385 4.7235 5.34734 4.41699 4.96924 4.41699V5.41699C4.79506 5.41699 4.65385 5.27579 4.65385 5.10161H5.65385Z" fill="#000C1F"/><path d="M9.03081 5.28622C8.92885 5.28622 8.84619 5.20357 8.84619 5.10161C8.84619 4.99965 8.92885 4.91699 9.03081 4.91699" stroke="#000C1F" stroke-linecap="round" stroke-linejoin="round"/><path d="M9.03076 4.78622C8.75462 4.78622 8.53076 5.01008 8.53076 5.28622C8.53076 5.56237 8.75462 5.78622 9.03076 5.78622V4.78622ZM9.03076 4.41699C8.75462 4.41699 8.53076 4.64085 8.53076 4.91699C8.53076 5.19313 8.75462 5.41699 9.03076 5.41699V4.41699ZM9.03076 5.78622C9.40886 5.78622 9.71538 5.47971 9.71538 5.10161H8.71538C8.71538 4.92743 8.85658 4.78622 9.03076 4.78622V5.78622ZM9.71538 5.10161C9.71538 4.7235 9.40886 4.41699 9.03076 4.41699V5.41699C8.85658 5.41699 8.71538 5.27579 8.71538 5.10161H9.71538Z" fill="#000C1F"/></svg>');

          // emojis loop
          for (let i = 0; i < emojis.length; i += 1) {
            const emoji = emojis[i];
            // eslint-disable-next-line import/no-dynamic-require
            editor.ui.registry.addIcon(emoji, `<img class="toolbar-emoji" src="${require(`../assets/images/emojis/${emoji}.png`)}" />`);
            editor.ui.registry.addButton(emoji, {
              icon: emoji,
              active: true,
              disabled: false,
              onAction() {
                editor.focus();
                const emptyLines = editor.getContent().match(/<div><\/div>/g) || [];
                if (!editor.getContent().length || emptyLines.length) {
                  editor.insertContent('<span class="qa-emoji-hidden-space">&nbsp;</span>&nbsp;');
                }

                if (editor.selection.getNode() && editor.selection.getNode().className && editor.selection.getNode().className.split(' ')[0] === 'emoji') {
                  editor.selection.setContent(`<span class="emoji emoji-${emoji} mceNonEditable"></span>`);
                } else {
                  editor.insertContent(
                    `<span class="emoji emoji-${emoji} mceNonEditable"></span>`,
                  );
                }

                charsCount();
                vm.closeToolbar(editor);
                editor.focus();
              },
            });
          }
        },
      },
    };
  },
  computed: {
    exceededCharsCheck() {
      return this.remainingChars < 0;
    },
  },
  mounted() {
    this.$root.$off('collapse');
    this.$root.$on('collapse', (el) => {
      this.focusOut(el);
    });
    this.$root.$off('expand');
    this.$root.$on('expand', (el) => {
      this.$nextTick(() => {
        this.focusIn(el, 'root');
      });
    });
    this.handleClickOutside = (e) => {
      const editor = document.querySelector('.qa-editor');

      if (e.target.closest('.qa-editor') && this.clickOutside) {
        this.clickOutside = false;
        editor.classList.add('expanded');
      }

      if (!e.target.closest('.qa-editor') && editor.classList.contains('expanded')) {
        editor.classList.remove('expanded');
      }
    };

    document.addEventListener('click', this.handleClickOutside);

    this.blurEditor = () => {
      const editor = document.querySelector('.qa-editor');

      if (editor && editor.classList.contains('expanded')) {
        editor.classList.remove('expanded');
        this.clickOutside = true;
      } else {
        this.clickOutside = false;
      }
    };

    window.addEventListener('blur', this.blurEditor);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
    window.removeEventListener('blur', this.blurEditor);
  },
  methods: {
    focusIn($e, origin) {
      let editor;
      if (origin === 'root') {
        editor = $e;
      } else {
        editor = $e.target.closest('.qa-editor');
      }

      if (editor && !this.clickOutside) {
        if (!editor.classList.contains('expanded')) {
          editor.classList.add('expanded');
        }
        if (editor.classList.contains('expanded')) {
          if (origin !== 'root') {
            this.$emit('expand', true);
          }
        }
      }
    },
    focusOut($e) {
      const editor = $e.target.bodyElement.offsetParent;

      if (editor.classList.contains('expanded')) {
        editor.classList.remove('expanded');
      }
      if (!editor.classList.contains('expanded')) {
        this.$emit('expand', false);
      }
    },

    addImageToEditor(editor, options) {
      const input = document.createElement('input');
      input.setAttribute('type', 'file');
      input.setAttribute('accept', '.png, .jpg, .jpeg');

      input.onchange = function onchange() {
        const [file] = this.files;

        const reader = new FileReader();
        // eslint-disable-next-line no-loop-func
        reader.onload = async function onload() {
          const id = `blobid${(new Date()).getTime()}`;
          // eslint-disable-next-line no-undef
          const { blobCache } = tinymce.activeEditor.editorUpload;
          const base64 = reader.result.split(',')[1];
          const blobInfo = blobCache.create(id, file, base64);
          blobCache.add(blobInfo);

          const src = blobInfo.blobUri();

          const styles = options?.customStyles;

          editor.insertContent(`<img src="${src}" style="${styles}">`);
        };
        reader.readAsDataURL(file);
      };

      window.addEventListener('focus', () => {
        document.activeElement.blur();
      }, { once: true });

      input.click();
    },
    clickImage(image) {
      setTimeout(() => {
        const handlesArr = this.getHandleElements();

        const addHideHandler = () => {
          setTimeout(() => {
            image.style.setProperty('visibility', 'hidden');
            this.getHandleElements().forEach((handle) => {
              handle.style.setProperty('display', 'none');
            });
          }, 25);
          // eslint-disable-next-line no-use-before-define
          this.addEventListeners([[window, 'mouseup', removeHideHandler]]);
        };
        const removeHideHandler = () => {
          image.style.removeProperty('visibility');
          image.removeAttribute('data-mce-style');

          handlesArr.forEach((handler) => {
            handler.style.removeProperty('display');
          });
          handlesArr.forEach((handler) => {
            handler.removeEventListener('mousedown', addHideHandler);
          });
          window.removeEventListener('mouseup', removeHideHandler);
          setTimeout(() => {
            this.getHandleElements().forEach((handle) => {
              this.addEventListeners([[handle, 'mousedown', addHideHandler]]);
            });
          }, 50);
        };
        handlesArr.forEach((handle) => {
          this.addEventListeners([[handle, 'mousedown', addHideHandler]]);
        });
      });
    },
    getHandleElements() {
      const handles = document.querySelectorAll('.mce-resizehandle');
      return Array.from(handles);
    },
    nodeChangeHandler(key, editor, api) {
      setTimeout(() => {
        let selectedNode = null;
        let formatter = null;

        if (editor.selection) {
          selectedNode = editor.selection.getStart();

          // formatter depends on the selection, so we need to check if there is a selection in editor before using it
          // maybe DS-3241 1-9 is resolved with this one
          formatter = editor.formatter ? editor.formatter.match(key) : null;
        }

        // Set the disabled state of the toolbar buttons
        const disabled = (selectedNode && (selectedNode.tagName === 'PRE' || selectedNode.closest('pre') || selectedNode.tagName === 'CODE' || selectedNode.tagName === 'IMG')) || this.isActiveKeyboard;
        api.setDisabled(disabled);

        // Set the active state of the toolbar buttons
        if (key !== 'images') {
          const isActive = {
            all: formatter,
            inlinecode: selectedNode && (selectedNode.tagName === 'CODE' && !selectedNode.closest('pre')),
            equation: selectedNode && (selectedNode.tagName === 'MATH-FIELD' || selectedNode.closest('math-field') || this.isActiveKeyboard),
          };

          if (typeof isActive[key] !== 'undefined') {
            api.setActive(isActive[key]);
          } else {
            api.setActive(isActive.all);
          }
        }
      }, 50);
    },
  },
};
</script>

<style lang="scss">
$emojis: smile, innocent, relieved, sunglasses, wink, smile-licky, wink-licky, tongue-out,
  stick-out-tongue, beaming-face, grinning, grinning-smiling-eyes,
  grinning-smiling-squinting-eyes, laughing, kissing, kissing-closed-eyes, inlove, shy,
  surprised, anguished, frowning, grimacing, fearful, anxious, screaming, confounded,
  dizzy, neutral, without-mouth, confused, frowning-eyebrows, disappointed, sad,
  persevering, confounded-normal, distraught, weary, expressionless, unamused, sleepy,
  crying, crying-loudly, downcast, steamy, angry, angry-red, smirk, devil, mask, smoked;

.qa-editor {
  position: relative;
  min-height: 80px;
  max-width: 100%;
  transition: all var(--duration) ease-out;
  color: var(--main-dark-light-4);
  background-color: var(--main-light);
  border: 1px solid var(--main-light-shade-4);
  border-radius: 4px;

  &[exceeded-chars] {
    border: 1.5px solid var(--main-red-shade-1);

    &.expanded {
      border: 1.5px solid var(--main-red-shade-1);
    }

    .char-limit {
      color: var(--main-red-shade-1);
    }
  }

  .char-limit {
    transition: all 0.2s ease-out;
    opacity: 0;
    position: absolute;
    right: 16px;
    bottom: 8px;
    color: var(--main-dark-light-4);
    font-size: 12px;
    font-weight: 400;
    line-height: 28px;
    z-index: 3;
  }

  .code-editor {
    height: 100%;
    display: flex;
    line-height: 1.5;
    border: 1px solid #CCC;
    background-color: var(--main-light);
    border-radius: 5px;

    .code-editor-wrapper {
      flex: 1;
    }

    textarea {
      width: 100%;
      overflow: auto;
      min-height: 308px;
      padding: 10px;
      border: none;
      resize: none;
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }

    > pre {
      overflow: auto;
      max-height: 308px;
      width: 50%;
      border-left: 1px solid #CCC;

      code {
        display: block;
        pointer-events: none;
        padding: 10px;
        white-space: pre-wrap;
        word-break: break-word;
      }
    }

    textarea::-webkit-scrollbar {
      width: 8px;
    }

    pre::-webkit-scrollbar {
      width: 0;
      display: none;
    }

    textarea::-webkit-scrollbar-thumb,
    pre::-webkit-scrollbar-thumb {
      background: #DBDBDB;
      border-radius: 10px;
    }

    textarea::-webkit-scrollbar-thumb:hover,
    pre::-webkit-scrollbar-thumb:hover {
      background: #CDCDCD;
    }
  }

  .mce-content-body {
    transition: min-height var(--duration) ease-out;
    min-height: 63px;
    cursor: text;
    margin: 0;
    padding-left: 16px;
    padding-right: 16px;
    padding-top: 16px;
    z-index: 1;
    line-height: 1.57;
    border-radius: 100px;
    color: var(--main-dark);

    pre[class*='language-'] {
      white-space: break-spaces;
      margin-top: var(--spacing-4);
      margin-bottom: var(--spacing-20);
      border: 1px solid #D0CFCE;
      border-radius: 10px;
    }

    code:not([class*="language-"]) {
      background: #F5F2F0;
      border: 1px solid #D0CFCE;
      padding: 3px;
      border-radius: 5px;
      color: var(--main-red) !important;
      display: inline;
      min-width: 30px;
      line-height: 19px;
      vertical-align: middle;
    }

    p {
      margin: 0;
      // overflow: hidden;
      // text-overflow: ellipsis;
      // max-width: 100%;
      // white-space: pre;
      word-wrap: break-word;

      pre[class*='language-'] {
        white-space: break-spaces;
        margin-top: var(--spacing-4);
        margin-bottom: var(--spacing-20);
        border: 1px solid #D0CFCE;
        border-radius: 10px;
      }

      > :not(.emoji, .external-anchor, img) {
        word-break: break-word;
        opacity: 1;
        color: var(--main-dark);
        visibility: visible;
        width: 98%;
        margin: auto;
        white-space: unset;
      }

      .external-anchor {
        color: var(--accent);
      }
    }

    &:before {
      content: atrr(placeholder);
      position: absolute;
      color: var(--main-light-shade-4) !important;
      line-height: 40px;
      top: 7px;
      left: unset !important;
      transition: all var(--duration) ease-out;
    }

    [contentEditable="false"][data-mce-selected] {
      cursor: default;
      outline: unset;

      // &.emoji:after {
      //   outline: 2px solid #979797;
      // }
    }

    img {
      display: block;
      height: auto;
      max-width: calc(100% - 20px);
      margin: 6px 10px 9px; // fix image resize handles
    }

    .mce-clonedresizable {
      // TODO: m.damyanov - When paragraphs are removed this needs to be updated to 100% - 28px
      max-width: calc(100% - 36px);
    }

    .mce-resize-helper {
      display: none !important;
    }
  }

  .editor-toolbar {
    bottom: 0;
    left: 0;
    width: 100%;
    border-radius: 10px;
    padding-left: 16px;
    background-color: var(--main-light);
    right: 0;
    opacity: 0;
    visibility: hidden;
    transition: all 0.2s ease-out;
    transition-delay: 0s;

    .tox.tox-silver-sink.tox-tinymce-aux {
      .tox-dialog-wrap {
        .tox-dialog--width-lg .tox-dialog__content-js .tox-dialog__body .tox-dialog__body-content .tox-form .tox-selectfield .focus:focus {
          border-color: var(--accent);
        }

        .tox-dialog {
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
          border-radius: 20px;
          width: 100%;
          max-width: 570px;

          .tox-dialog__header {
            padding: 43px 55px 0 55px;

            .tox-dialog__title {
              margin: auto;
              font-weight: 600;
              font-size: 28px;
              line-height: 120%;
            }

            .tox-button.tox-button--icon.tox-button--naked {
              position: absolute;
              top: 20px;
              right: 20px;
              border-style: unset;

              .tox-icon {
                svg {
                  fill: var(--main-light-shade-4);
                }
              }

              &:hover {
                background-color: unset;
                border: unset;
              }
            }
          }

          .tox-dialog__content-js {
            min-height: 0;

            .tox-dialog__body {
              .tox-dialog__body-content {
                overflow: hidden;
                padding: var(--spacing-24) 55px 0 55px;

                .tox-form {
                  flex-direction: column-reverse;

                  .tox-form__group {
                    margin-bottom: 0;

                    &:last-of-type {
                      margin-bottom: var(--spacing-24);
                    }

                    input {
                      height: 44px;
                    }

                    .tox-label {
                      line-height: 167%;
                      font-size: 14px;
                      font-weight: 500;
                      color: var(--main-dark-light-4);
                      margin-bottom: var(--spacing-4);
                    }

                    .tox-textfield {
                      border: 1px solid var(--main-light-shade-4);
                      border-radius: 4px;
                      line-height: 167%;
                      color: var(--main-dark);

                      &:focus {
                        border: 2px solid var(--accent);
                      }
                    }
                  }
                }
              }
            }
          }

          .tox-dialog__footer {
            padding: var(--spacing-24) 55px var(--spacing-44);
            border-top: unset;

            .tox-button {
              font-size: 16px;
              width: 178px;
              height: 40px;
              background-color: var(--accent);
              border-color: var(--accent);

              &:hover {
                background-color: var(--accent-shade-1);
                border-color: var(--accent-shade-1);
              }

              &:active {
                background-color: var(--accent-shade-2);
                border-color: var(--accent-shade-2);
              }
            }

            .tox-button.tox-button--secondary {
              font-weight: 600;
              font-size: 16px;
              margin-right: var(--spacing-8);
              color: var(--main-light-shade-4);
              background-color: transparent;
              border: 0;
              width: auto;

              &.clear-btn {
                padding-left: 0;
              }

              &:hover,
              &:active {
                color: var(--main-dark-light-4);
              }
            }
          }
        }

        .tox-dialog--width-lg {
          max-width: 976px;
          height: 637px;

          .textarea-wrapper {
            height: 100%;
          }

          .tox-dialog__header {
            padding: var(--spacing-44) 55px 0 55px;

            .tox-dialog__title {
              line-height: 119%;
            }
          }

          .tox-dialog__content-js .tox-dialog__body .tox-dialog__body-content {
            padding: var(--spacing-24) 55px 0 55px;

            svg {
              display: none;
            }

            .tox-form {
              flex-direction: column;

              .tox-form__group {
                &:last-of-type {
                  margin-top: var(--spacing-24);
                  margin-bottom: 0;

                  textarea {
                    overflow: hidden;

                    &.scroll {
                      overflow: auto;
                    }
                  }
                }

                .tox-label {
                  line-height: 164%;
                }
              }

              .tox-form__group--stretched .tox-textarea:focus {
                height: 100%;
                border: 1px solid var(--accent);
              }

              .tox-selectfield {
                position: relative;

                select {
                  border-radius: 5px;
                  border-color: transparent;
                  box-shadow: 0 4px 8px rgba(16, 20, 28, 0.15);
                  padding: 7px 12px;
                }

                &:before {
                  content: url("../assets/svg/icon-arrow-dropdown.svg");
                  position: absolute;
                  right: 12px;
                  transform: rotate(180deg);
                  top: calc(50% - 8px);
                }
              }
            }
          }

          .tox-dialog__footer {
            padding: var(--spacing-24) 55px var(--spacing-44);
            border-top: unset;

            .tox-button {
              width: 178px;
              height: 40px;
              background-color: var(--accent);
              border-color: var(--accent);

              &:hover {
                background-color: var(--accent-shade-1);
                border-color: var(--accent-shade-1);
              }

              &:active {
                background-color: var(--accent-shade-2);
                border-color: var(--accent-shade-2);
              }
            }

            .tox-button.tox-button--secondary {
              font-weight: 600;
              font-size: 16px;
              margin-right: var(--spacing-8);
              color: var(--main-light-shade-4);
              background-color: transparent;
              border: 0;
              width: auto;

              &.clear-btn {
                padding-left: 0;
              }

              &:hover,
              &:active {
                color: var(--main-dark-light-4);
              }
            }
          }
        }
      }
    }

    .tox.tox-tinymce-inline {
      .tox-toolbar-overlord,
      .tox-toolbar__primary {
        background: none;
      }

      .tox-toolbar__primary {
        .tox-toolbar__group {
          &:first-of-type {
            padding-left: 0;
          }
        }

        > div {
          padding-left: var(--spacing-12);
          border: none;
          position: relative;

          &:after {
            content: "";
            display: block;
            position: absolute;
            top: 0;
            left: 0;
            width: 1px;
            height: 16px;
            background-color: var(--main-light-shade-3);
          }

          &:first-child:after {
            content: none;
          }

          &:last-child {
            flex: auto;
          }
        }
      }

      .tox-editor-header {
        border: none;
      }

      .tox-tbtn {
        width: var(--spacing-16);
        height: var(--spacing-16);
        margin: 0 var(--spacing-12) 0 0;

        &:hover,
        &.tox-tbtn--enabled {
          background: none;
          cursor: pointer;

          svg {
            fill: none;

            path[fill] {
              fill: var(--accent);
            }

            path[stroke] {
              stroke: var(--accent);
            }
          }
        }

        svg {
          fill: none;
        }
      }
    }

    .toolbar-emoji {
      object-fit: cover;
      object-position: center;
      width: 100%;
      height: 100%;
    }

    .tox.tox-tinymce-aux {
      .tox-toolbar__overflow {
        bottom: unset !important;
        background: var(--main-light);
        box-shadow: 0 16px 30px rgba(0, 12, 31, 0.15);
        border-radius: 20px;
        left: 232px !important;
        border: none;
        max-width: 550px !important;
        width: 100%;
        top: -10px;

        .tox-toolbar__group {
          padding: 0;
        }
      }

      .tox-tbtn {
        width: auto;
        height: auto;
        margin: 0;
        cursor: pointer;

        .tox-icon {
          width: 52px;
          height: 52px;
          padding: 10px;
          box-sizing: border-box;
          transition: all 300ms ease-out;
        }

        &:hover {
          background: none;

          .tox-icon {
            padding: 0;
          }
        }
      }
    }
  }

  .edit-link-popup {
    position: absolute;
    background: var(--main-light);
    z-index: 1;
    filter: drop-shadow(0 16px 30px rgba(0, 12, 31, 0.15));
    border-radius: 10px;
    padding: var(--spacing-20) var(--spacing-12) var(--spacing-16) var(--spacing-12);
    max-width: 440px;
    word-break: break-word;
    max-height: 150px;

    &:after {
      content: "";
      width: 0;
      height: 0;
      position: absolute;
      bottom: -8px;
      border-left: 7px solid transparent;
      border-right: 7px solid transparent;
      border-top: 8px solid var(--main-light);
    }

    .link,
    .title {
      font-weight: 500;
      line-height: 27px;
      overflow: hidden;
      text-overflow: ellipsis;
      display: block;
      white-space: nowrap;
    }

    .title {
      color: var(--main-dark);
      margin-bottom: 4px;
      max-width: 95%;
    }

    .link {
      color: var(--accent-shade-2);
      margin-bottom: 16px;

      &:hover {
        text-decoration: underline;
      }
    }

    .btn-primary-light {
      margin-right: 20px;
    }

    .btn-text {
      color: var(--main-red-light-3);
    }

    svg {
      position: absolute;
      top: 12px;
      right: 12px;
      width: 12px;
      height: 12px;
      cursor: pointer;

      path {
        fill: var(--main-light-shade-4);
      }
    }
  }

  &[value]:not([value=""]) {
    .mce-content-body:after {
      opacity: 0;
    }
  }

  &.expanded.reply {
    max-height: 113px;

    .mce-content-body {
      max-height: 113px;
    }
  }

  &.expanded {
    border-radius: 4px;
    border-color: var(--accent);
    background-color: var(--main-light);
    max-height: 100% !important;

    .char-limit {
      opacity: 1;
    }

    .mce-content-body {
      height: 100%;
      min-height: 162px;
      margin-bottom: var(--spacing-12);
      opacity: 1;
      overflow-y: auto;
      border-radius: 0;
      color: var(--main-dark);

      &::-webkit-scrollbar {
        width: 8px;
      }

      /* Handle */
      &::-webkit-scrollbar-thumb {
        background: #DBDBDB;
        border-radius: 10px;
      }

      /* Handle on hover */
      &::-webkit-scrollbar-thumb:hover {
        background: #CDCDCD;
      }

      pre[class*='language-'] {
        white-space: break-spaces;
        margin-bottom: var(--spacing-20);
      }

      a {
        color: var(--accent-shade-2);
        font-weight: 600;
        cursor: pointer;
      }

      > *:not(.mce-resizehandle):not(.mce-resize-helper):not(.emoji):not(a):not(img) {
        word-break: break-word;
        opacity: 1;
        color: var(--main-dark);
        visibility: visible;
        width: 98%;
        // white-space: unset;
      }

      &:before {
        opacity: 0;
        visibility: hidden;
      }
    }

    .editor-toolbar {
      transition-delay: var(--duration);
      transition: all var(--duration) ease-out;
      opacity: 1;
      margin-bottom: 14px;
      visibility: visible;
      z-index: 2;
    }
  }

  &.comment {
    .mce-content-body {
      &:after {
        content: "Leave a comment";
      }
    }
  }

  &.in-video {
    .editor-toolbar .tox.tox-tinymce-aux .tox-toolbar__overflow {
      left: 130px !important;
      max-width: 329px !important;
      max-height: 260px !important;
    }
  }
}

.qa-emoji-hidden-space {
  display: none;
}

.emoji {
  display: inline-block;
  position: relative;
  width: 24px;
  height: 18px;
  font-size: 0;
  margin-right: 2px;

  &:after {
    content: "";
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: 24px;
    height: 24px;
    background: none;
    background-size: cover;
  }

  @each $emoji in $emojis {
    &.emoji-#{$emoji}:after {
      background-image: url(../assets/images/emojis/#{$emoji}.png);
    }
  }
}

@include bp($bp-laptop) {
  .qa-editor {
    .mce-content-body {
      max-height: 290px;
    }

    .editor-toolbar .tox.tox-silver-sink.tox-tinymce-aux .tox-dialog-wrap {
      .tox-dialog .tox-dialog__header .tox-dialog__title {
        font-size: 24px;
      }

      .tox-dialog--width-lg {
        max-width: 826px;
        height: 632px;
      }
    }
  }
}

@include bp($bp-mobile) {
  .qa-editor {
    &.expanded {
      padding: 9px 9px var(--spacing-12) 9px;

      .mce-content-body {
        height: 162px;
        min-height: unset;

        > *:not(.mce-resizehandle):not(.mce-resize-helper) {
          width: 100%;
          margin-left: 0;
          margin-bottom: 0;
        }

        p {
          img {
            width: 100%;
            margin-left: 0;
          }
        }
      }

      .editor-toolbar {
        line-height: 22px;

        .tox.tox-tinymce-aux {
          .tox-toolbar__overflow {
            max-width: unset !important;
            width: auto;
            top: 15px;
            left: 0 !important;
          }
        }

        .tox.tox-tinymce-inline {
          .tox-toolbar__primary {
            > div {
              padding-left: var(--spacing-12);
              padding-right: 0;
              padding-bottom: 0;
            }

            > div:first-of-type {
              padding-left: 0;
            }
          }
        }

        .tox.tox-silver-sink.tox-tinymce-aux {
          .tox-dialog-wrap {
            .tox-dialog {
              width: 580px;
              align-self: unset;

              .tox-dialog__header .tox-dialog__title {
                font-size: 21px;
              }

              .tox-dialog__footer {
                padding: var(--spacing-24) var(--spacing-70) var(--spacing-44);
                justify-content: center;

                .tox-dialog__footer-end {
                  width: 100%;
                }
              }
            }
          }
        }
      }
    }

    .tox-form {
      flex-grow: 0;
      flex-basis: 100%;
      min-height: 0;

      .tox-form__group.tox-form__group--stretched {
        flex-grow: 0;
        flex-basis: 100%;
        min-height: 0;
      }
    }

    .code-editor {
      flex-direction: column;
      flex-grow: 0;
      flex-basis: 100%;
      min-height: 0;

      .code-editor-wrapper {
        display: flex;
        flex-grow: 0;
        flex-basis: 100%;

        .textarea-wrapper {
          flex: 0 0 100%;
          overflow: hidden;
        }
      }

      textarea {
        max-height: 100%;
        min-height: 0;
        height: 100%;
        width: 100%;
        overflow: auto;
      }

      > pre {
        max-height: 50%;
        height: 100%;
        width: 100%;
        border-left: unset;
        border-top: 1px solid #CCC;
      }
    }
  }
}

@include bp($bp-phone) {
  .qa-editor {
    &.expanded {
      .editor-toolbar {
        .tox.tox-tinymce-inline {
          .tox-toolbar__primary {
            > div {
              padding-left: var(--spacing-12);
              padding-bottom: 0;
            }
          }
        }

        .tox.tox-silver-sink.tox-tinymce-aux {
          .tox-dialog-wrap {
            .tox-dialog {
              max-width: 327px;
              align-self: unset;
              max-height: 386px;
              height: 100%;
              margin-top: var(--spacing-lg);

              &.tox-confirm-dialog {
                max-height: 200px;
              }

              .tox-dialog__header {
                padding: 38px var(--spacing-32) 0 var(--spacing-32);

                .tox-button.tox-button--icon.tox-button--naked {
                  height: auto;
                  top: 12px;
                  right: 11px;
                  display: flex;
                  align-items: center;
                  justify-content: flex-end;
                }
              }

              .tox-dialog__content-js {
                .tox-dialog__body {
                  .tox-dialog__body-content {
                    padding: var(--spacing-32) var(--spacing-32) 0 var(--spacing-32);
                  }
                }
              }

              .tox-dialog__footer {
                padding: var(--spacing-32) var(--spacing-32) var(--spacing-44);
                justify-content: space-between;

                .tox-dialog__footer-end {
                  justify-content: flex-end;
                }

                .tox-button {
                  width: 100px;
                  margin: 0;

                  &.tox-button--secondary {
                    margin: 0 var(--spacing-20) 0 0;
                    width: auto;
                    padding: 0;
                  }
                }
              }
            }

            .tox-dialog--width-lg {
              max-height: 650px;
              height: 100%;
              margin: auto;

              .tox-dialog__header {
                padding: 43px var(--spacing-32) 0 var(--spacing-32);

                .tox-dialog__title {
                  line-height: 119%;
                }
              }

              .tox-dialog__content-js {
                .tox-dialog__body {
                  .tox-dialog__body-content {
                    padding: var(--spacing-24) var(--spacing-32) 0 var(--spacing-32);
                  }
                }
              }

              .tox-dialog__footer {
                padding: var(--spacing-24) var(--spacing-32) var(--spacing-44);
              }
            }
          }
        }
      }

      .code-editor {
        flex-direction: column;

        textarea {
          overflow: auto;
          width: 100%;
          max-height: 100%;
        }

        + pre {
          width: 100%;
          max-height: 50%;
          border-left: 0;
          border-top: 1px solid #CCC;
        }
      }
    }
  }
}

@media screen and (max-height: 700px) {
  .qa-editor {
    .code-editor {
      > pre {
        max-height: 236px;
      }

      textarea {
        min-height: 100%;
      }
    }

    .editor-toolbar .tox.tox-silver-sink.tox-tinymce-aux .tox-dialog-wrap .tox-dialog--width-lg {
      max-height: calc(100% - 140px);
    }
  }
}
</style>
